import React from 'react';
import { ui, btnGhost } from './canvasUi';
import { IconClose, IconPlus } from './icons';
import {
    setCell, setHeader, addRow, addColumn, removeRow, removeColumn,
    canAddRow, canAddColumn, MAX_COLS, MAX_ROWS,
} from './manualTable';

/**
 * The editable grid for a manual table, used in two places: inside the config
 * drawer and directly on the block card.
 *
 * One component rather than two similar ones — they edit the same block, often
 * in the same sitting, so a divergence between them would be a bug the user
 * sees as the table changing depending on where they typed.
 *
 * `compact` trims the chrome for the card: no column-delete buttons (columns
 * are structural and rarer — the drawer owns that) and tighter cells, so the
 * grid still reads as the dashboard block it is rather than a form.
 */

const s = {
    scroll: { overflow: 'auto', border: ui.border, borderRadius: ui.radiusSm, background: ui.surface },
    table: { borderCollapse: 'collapse', width: '100%', fontSize: '12.5px' },
    th: { border: `1px solid ${ui.hairline}`, padding: 0, background: '#fafaf9', minWidth: '96px' },
    td: { border: `1px solid ${ui.hairline}`, padding: 0, minWidth: '96px' },
    cellInput: (compact) => ({
        width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
        padding: compact ? '5px 7px' : '7px 8px', fontSize: '12.5px', fontFamily: 'inherit',
        background: 'transparent', color: ui.ink,
    }),
    headInput: { fontWeight: 700 },
    gutter: { border: `1px solid ${ui.hairline}`, background: '#fafaf9', width: '26px', padding: 0, textAlign: 'center' },
    xBtn: { border: 'none', background: 'none', cursor: 'pointer', color: ui.ink2, lineHeight: 1, padding: '3px', display: 'inline-flex', borderRadius: '4px' },
    actions: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' },
    smallBtn: { ...btnGhost, padding: '5px 10px', fontSize: '12px' },
    hint: { fontSize: '11.5px', color: ui.ink2, marginTop: '6px', display: 'block' },
    empty: { fontSize: '12.5px', color: ui.ink2 },
};

export default function ManualTableGrid({ cfg, onChange, compact = false }) {
    const columns = Array.isArray(cfg?.columns) ? cfg.columns : [];
    const rows = Array.isArray(cfg?.rows) ? cfg.rows : [];
    const rowLimit = !canAddRow(cfg);
    const colLimit = !canAddColumn(cfg);

    return (
        <div>
            {columns.length === 0 ? (
                <span style={s.empty}>No columns yet — add one to start.</span>
            ) : (
                <div style={s.scroll}>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.gutter} aria-label="Row controls" />
                                {columns.map((h, c) => (
                                    <th key={c} style={s.th}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <input className="cvx-focus" style={{ ...s.cellInput(compact), ...s.headInput }} value={h}
                                                aria-label={`Column ${c + 1} name`}
                                                onChange={(e) => onChange(setHeader(cfg, c, e.target.value))} />
                                            {!compact && (
                                                <button type="button" style={s.xBtn} title="Remove column"
                                                    aria-label={`Remove column ${h}`}
                                                    onClick={() => onChange(removeColumn(cfg, c))}><IconClose size={11} /></button>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, r) => (
                                <tr key={r}>
                                    <td style={s.gutter}>
                                        <button type="button" style={s.xBtn} title="Remove row"
                                            aria-label={`Remove row ${r + 1}`}
                                            onClick={() => onChange(removeRow(cfg, r))}><IconClose size={11} /></button>
                                    </td>
                                    {columns.map((_, c) => (
                                        <td key={c} style={s.td}>
                                            <input className="cvx-focus" style={s.cellInput(compact)} value={row?.[c] ?? ''}
                                                aria-label={`Row ${r + 1}, ${columns[c]}`}
                                                onChange={(e) => onChange(setCell(cfg, r, c, e.target.value))} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div style={s.actions}>
                <button type="button" className="cvx-ghost cvx-focus" style={s.smallBtn}
                    onClick={() => onChange(addRow(cfg))} disabled={rowLimit}
                    data-testid="canvas-mt-add-row"><IconPlus size={12} /> Add row</button>
                <button type="button" className="cvx-ghost cvx-focus" style={s.smallBtn}
                    onClick={() => onChange(addColumn(cfg))} disabled={colLimit}
                    data-testid="canvas-mt-add-col"><IconPlus size={12} /> Add column</button>
            </div>

            {(rowLimit || colLimit) && (
                <span style={s.hint}>
                    {rowLimit ? `Up to ${MAX_ROWS} rows. ` : ''}
                    {colLimit ? `Up to ${MAX_COLS} columns. ` : ''}
                    For anything larger, use a Table block with a data source.
                </span>
            )}
        </div>
    );
}
